import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { httpClient } from '@/infrastructure/http';
import { useToast } from '@/presentation/contexts/ToastContext';
import { getCountryLabel } from '@/shared/constants/countries';

interface SolicitudTenant {
  id: number;
  primer_nombre: string;
  apellido: string;
  correo: string;
  telefono: string | null;
  nombre_clinica: string;
  zona_horaria: string;
  pais: string | null;
  nombre_consultorio: string;
  direccion_consultorio: string;
  telefono_consultorio: string;
  estado: string;
  notas_admin: string | null;
  notas_internas: string | null;
  flagged: boolean;
  revisado_por: string | null;
  revisado_en: string | null;
  creado_en: string;
  actualizado_en: string;
}

interface AuditoriaEntry {
  id: number;
  accion: string;
  usuario: string;
  detalle: string | null;
  fecha: string;
}

type EstadoFilter = 'pendiente' | 'aprobado' | 'rechazado';

export const SolicitudesPendientesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { success, error: showError, warning } = useToast();

  const [solicitudes, setSolicitudes] = useState<SolicitudTenant[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [estadoFilter, setEstadoFilter] = useState<EstadoFilter>('pendiente');
  const [selected, setSelected] = useState<SolicitudTenant | null>(null);

  // Modal de rechazo
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectNotas, setRejectNotas] = useState('');
  const [rejectTarget, setRejectTarget] = useState<SolicitudTenant | null>(null);

  // Modal de aprobación
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveTarget, setApproveTarget] = useState<SolicitudTenant | null>(null);

  // Modal de notas internas
  const [showNotasModal, setShowNotasModal] = useState(false);
  const [notasText, setNotasText] = useState('');
  const [notasFlagged, setNotasFlagged] = useState(false);
  const [notasTarget, setNotasTarget] = useState<SolicitudTenant | null>(null);
  const [savingNotas, setSavingNotas] = useState(false);

  // Procesando
  const [processingId, setProcessingId] = useState<number | null>(null);

  // Auditoría
  const [auditoria, setAuditoria] = useState<AuditoriaEntry[]>([]);
  const [auditoriaLoading, setAuditoriaLoading] = useState(false);
  const [showAuditoria, setShowAuditoria] = useState(false);

  const loadSolicitudes = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await httpClient.get<SolicitudTenant[]>(`/solicitudes-tenant?estado=${estadoFilter}`);
      setSolicitudes(res.data);
      setSelected(prev => {
        if (!prev) return null;
        return res.data.find(s => s.id === prev.id) ?? null;
      });
    } catch {
      showError(t('pages.solicitudes.errorLoading'));
    } finally {
      setIsLoading(false);
    }
  }, [estadoFilter, showError, t]);

  useEffect(() => {
    loadSolicitudes();
  }, [loadSolicitudes]);

  const loadAuditoria = useCallback(async (id: number) => {
    setAuditoriaLoading(true);
    try {
      const res = await httpClient.get<AuditoriaEntry[]>(`/solicitudes-tenant/${id}/auditoria`);
      setAuditoria(res.data);
    } catch {
      setAuditoria([]);
    } finally {
      setAuditoriaLoading(false);
    }
  }, []);

  const openApproveModal = (solicitud: SolicitudTenant) => {
    setApproveTarget(solicitud);
    setShowApproveModal(true);
  };

  const selectSolicitud = (s: SolicitudTenant | null) => {
    setSelected(s);
    if (s) loadAuditoria(s.id);
    else { setAuditoria([]); setShowAuditoria(false); }
  };

  const handleAprobar = async () => {
    if (!approveTarget) return;
    setProcessingId(approveTarget.id);
    try {
      // Crear BD + schema + semilla suele tardar > 10s (timeout global del httpClient).
      const res = await httpClient.post<{
        credentialsEmailSent?: boolean;
        linkedExistingAccount?: boolean;
        emailWarning?: string;
      }>(
        `/solicitudes-tenant/${approveTarget.id}/aprobar`,
        undefined,
        { timeout: 180_000 }
      );
      if (res.data.emailWarning) {
        warning(res.data.emailWarning, 8000);
      } else if (res.data.linkedExistingAccount) {
        success(t('pages.solicitudes.approvedSuccessLinked', {
          nombre: approveTarget.nombre_clinica,
          email: approveTarget.correo,
        }));
      } else {
        success(t('pages.solicitudes.approvedSuccess', { nombre: approveTarget.nombre_clinica }));
      }
      setShowApproveModal(false);
      setApproveTarget(null);
      setSelected(null);
      await loadSolicitudes();
    } catch (err: unknown) {
      const axiosErr = err as { code?: string; message?: string; response?: { data?: { message?: string } } };
      const timedOut = axiosErr.code === 'ECONNABORTED' || /timeout/i.test(axiosErr.message ?? '');
      const msg = axiosErr.response?.data?.message;
      showError(msg || (timedOut ? t('pages.solicitudes.errorApprovingTimeout') : t('pages.solicitudes.errorApproving')));
    } finally {
      setProcessingId(null);
    }
  };

  const openRejectModal = (solicitud: SolicitudTenant) => {
    setRejectTarget(solicitud);
    setRejectNotas('');
    setShowRejectModal(true);
  };

  const openNotasModal = (solicitud: SolicitudTenant) => {
    setNotasTarget(solicitud);
    setNotasText(solicitud.notas_internas ?? '');
    setNotasFlagged(solicitud.flagged);
    setShowNotasModal(true);
  };

  const handleSaveNotas = async () => {
    if (!notasTarget) return;
    setSavingNotas(true);
    try {
      await httpClient.patch(`/solicitudes-tenant/${notasTarget.id}/notas`, {
        notas_internas: notasText.trim() || null,
        flagged: notasFlagged,
      });
      success(t('pages.solicitudes.notasSaved'));
      setShowNotasModal(false);
      await loadSolicitudes();
      if (notasTarget) loadAuditoria(notasTarget.id);
    } catch {
      showError(t('pages.solicitudes.errorSavingNotas'));
    } finally {
      setSavingNotas(false);
      setNotasTarget(null);
    }
  };

  const handleRechazar = async () => {
    if (!rejectTarget) return;
    setProcessingId(rejectTarget.id);
    setShowRejectModal(false);
    try {
      await httpClient.post(`/solicitudes-tenant/${rejectTarget.id}/rechazar`, { notas: rejectNotas });
      success(t('pages.solicitudes.rejectedSuccess'));
      setSelected(null);
      await loadSolicitudes();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      showError(msg || t('pages.solicitudes.errorRejecting'));
    } finally {
      setProcessingId(null);
      setRejectTarget(null);
    }
  };

  const fmtDate = (d: string | null) => {
    if (!d) return '—';
    return new Intl.DateTimeFormat('es-AR', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(d));
  };

  const estadoBadge = (estado: string) => {
    const base = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
    if (estado === 'pendiente') return `${base} bg-yellow-100 text-yellow-800 dark:bg-yellow-800/30 dark:text-yellow-300`;
    if (estado === 'aprobado') return `${base} bg-green-100 text-green-800 dark:bg-green-800/30 dark:text-green-300`;
    return `${base} bg-red-100 text-red-800 dark:bg-red-800/30 dark:text-red-300`;
  };

  const accionBadge = (accion: string) => {
    if (accion === 'aprobado') return 'bg-green-100 text-green-800 dark:bg-green-800/30 dark:text-green-300';
    if (accion === 'rechazado') return 'bg-red-100 text-red-800 dark:bg-red-800/30 dark:text-red-300';
    if (accion === 'flagged') return 'bg-amber-100 text-amber-800 dark:bg-amber-800/30 dark:text-amber-300';
    if (accion === 'unflagged' || accion === 'nota_eliminada') return 'bg-slate-100 text-slate-700 dark:bg-gray-700 dark:text-slate-300';
    return 'bg-teal-100 text-teal-800 dark:bg-teal-800/30 dark:text-teal-300';
  };

  return (
    <div className="panel-page h-full flex flex-col overflow-hidden !pb-3">
      {/* Filtro por estado */}
      <div className="flex-shrink-0 flex gap-1.5 mb-2 flex-wrap">
        {([
          { key: 'pendiente' as const, label: 'Pendientes' },
          { key: 'aprobado' as const, label: 'Aprobados' },
          { key: 'rechazado' as const, label: 'Rechazados' },
        ]).map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => { setEstadoFilter(key); setSelected(null); }}
            className={`panel-chip px-2.5 py-1.5 capitalize transition-colors ${
              estadoFilter === key
                ? 'bg-teal-600 text-white'
                : 'bg-white dark:bg-gray-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-gray-700 hover:bg-slate-50 dark:hover:bg-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
        <button
          type="button"
          onClick={loadSolicitudes}
          className="btn btn-sm btn-secondary ml-auto"
          title={t('common.search')}
        >
          ↻ Actualizar
        </button>
      </div>

      {/* Layout: lista + detalle */}
      <div className="flex flex-col md:flex-row gap-4 flex-1 min-h-0">
        {/* Lista */}
        <div
          className={`flex flex-col panel-card overflow-hidden transition-all duration-200 ${
            selected ? 'hidden md:flex md:w-80 md:flex-shrink-0' : 'flex-1'
          }`}
        >
          {isLoading ? (
            <div className="flex-1 flex items-center justify-center text-slate-400 dark:text-slate-500">
              {t('common.loading')}
            </div>
          ) : solicitudes.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-slate-400 dark:text-slate-500 px-4 text-center text-sm">
              {t('pages.solicitudes.noResults', { estado: t(`pages.solicitudes.estados.${estadoFilter}`) })}
            </div>
          ) : (
            <div className="overflow-y-auto flex-1">
              {solicitudes.map(s => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => selectSolicitud(s)}
                  className={`w-full text-left px-3.5 py-2.5 border-b border-slate-100 dark:border-gray-700 hover:bg-slate-50 dark:hover:bg-gray-700/50 transition-colors ${
                    selected?.id === s.id
                      ? 'bg-teal-50 dark:bg-teal-900/20 border-l-4 border-l-teal-500'
                      : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        {s.flagged && (
                          <span className="text-amber-500 text-xs" title={t('pages.solicitudes.flagged')}>&#9873;</span>
                        )}
                        <p className="font-semibold text-sm text-slate-900 dark:text-white truncate">
                          {s.nombre_clinica}
                        </p>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                        {s.primer_nombre} {s.apellido}
                      </p>
                      <p className="text-xs text-slate-400 dark:text-slate-500 truncate">
                        {s.correo}
                      </p>
                    </div>
                    <div className="flex-shrink-0 mt-0.5">
                      <span className={estadoBadge(s.estado)}>
                        {t(`pages.solicitudes.estados.${s.estado}`)}
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                    {fmtDate(s.creado_en)}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Panel de detalle */}
        {selected && (
          <div className="flex-1 flex flex-col panel-card overflow-hidden min-h-0">
            {/* Contenido scrolleable */}
            <div className="flex-1 overflow-y-auto">
              {/* Header del detalle */}
              <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-3 border-b border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="md:hidden flex-shrink-0 rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-gray-700"
                  aria-label={t('common.back')}
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                {/* Nombre + badge */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white truncate">
                      {selected.nombre_clinica}
                    </h2>
                    <span className={estadoBadge(selected.estado)}>
                      {t(`pages.solicitudes.estados.${selected.estado}`)}
                    </span>
                  </div>
                </div>
                {/* Botones de acción + cerrar */}
                <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                  {selected.estado === 'pendiente' && (
                    <>
                      <button
                        type="button"
                        onClick={() => openApproveModal(selected)}
                        disabled={processingId === selected.id}
                        className="btn btn-sm bg-green-600 text-white hover:bg-green-700 disabled:bg-green-400 focus:ring-green-500"
                      >
                        {processingId === selected.id
                          ? t('pages.solicitudes.processing')
                          : t('pages.solicitudes.actions.approve')}
                      </button>
                      <button
                        type="button"
                        onClick={() => openRejectModal(selected)}
                        disabled={processingId === selected.id}
                        className="btn btn-sm btn-danger disabled:bg-red-400"
                      >
                        {t('pages.solicitudes.actions.reject')}
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => openNotasModal(selected)}
                    title={t('pages.solicitudes.notasModal.buttonTitle')}
                    className={`btn btn-sm ${
                      selected.flagged
                        ? 'border border-amber-400 text-amber-600 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/40'
                        : 'btn-secondary'
                    }`}
                  >
                    <span>{selected.flagged ? '⚑' : '⚐'}</span>
                    <span>{t('pages.solicitudes.notasModal.addNotas')}</span>
                    {selected.notas_internas && (
                      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-500 text-white text-[10px] font-bold leading-none">!</span>
                    )}
                  </button>
                  <div className="w-px h-6 bg-slate-200 dark:bg-gray-600 mx-1" />
                  <button
                    type="button"
                    onClick={() => selectSolicitud(null)}
                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-xl leading-none p-1"
                    title={t('common.close')}
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Datos personales */}
              <section>
                <div className="px-5 py-2 bg-slate-50 dark:bg-gray-900/50 border-b border-slate-200 dark:border-gray-700">
                  <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('pages.solicitudes.sections.personalData')}
                  </h3>
                </div>
                <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 border-b border-slate-200 dark:border-gray-700">
                  <DetailRow label={t('pages.solicitudes.fields.firstName')} value={selected.primer_nombre} />
                  <DetailRow label={t('pages.solicitudes.fields.lastName')} value={selected.apellido} />
                  <DetailRow label={t('pages.solicitudes.fields.email')} value={selected.correo} />
                  <DetailRow label={t('pages.solicitudes.fields.phone')} value={selected.telefono ?? '—'} />
                </div>
              </section>

              {/* Datos de la clínica */}
              <section>
                <div className="px-5 py-2 bg-slate-50 dark:bg-gray-900/50 border-b border-slate-200 dark:border-gray-700">
                  <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('pages.solicitudes.sections.clinicData')}
                  </h3>
                </div>
                <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 border-b border-slate-200 dark:border-gray-700">
                  <DetailRow label={t('pages.solicitudes.fields.clinicName')} value={selected.nombre_clinica} />
                  <DetailRow label={t('pages.solicitudes.fields.timezone')} value={selected.zona_horaria} />
                  <DetailRow
                    label={t('pages.tenants.country')}
                    value={getCountryLabel(selected.pais, i18n.language) || t('pages.tenants.countryUnknown')}
                  />
                </div>
              </section>

              {/* Datos del consultorio */}
              <section>
                <div className="px-5 py-2 bg-slate-50 dark:bg-gray-900/50 border-b border-slate-200 dark:border-gray-700">
                  <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('pages.solicitudes.sections.officeData')}
                  </h3>
                </div>
                <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4 border-b border-slate-200 dark:border-gray-700">
                  <DetailRow label={t('pages.solicitudes.fields.officeName')} value={selected.nombre_consultorio} />
                  <DetailRow label={t('pages.solicitudes.fields.officeAddress')} value={selected.direccion_consultorio} />
                  <DetailRow label={t('pages.solicitudes.fields.officePhone')} value={selected.telefono_consultorio} />
                </div>
              </section>

              {/* Metadatos */}
              <section>
                <div className="px-5 py-2 bg-slate-50 dark:bg-gray-900/50 border-b border-slate-200 dark:border-gray-700">
                  <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('pages.solicitudes.sections.metadata')}
                  </h3>
                </div>
                <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                  <DetailRow label={t('pages.solicitudes.fields.createdAt')} value={fmtDate(selected.creado_en)} />
                  {selected.revisado_en && (
                    <DetailRow label={t('pages.solicitudes.fields.reviewedAt')} value={fmtDate(selected.revisado_en)} />
                  )}
                  {selected.revisado_por && (
                    <DetailRow label={t('pages.solicitudes.fields.reviewedBy')} value={selected.revisado_por} />
                  )}
                  {selected.notas_admin && (
                    <div className="col-span-2">
                      <DetailRow label={t('pages.solicitudes.fields.adminNotes')} value={selected.notas_admin} />
                    </div>
                  )}
                </div>
              </section>

              {/* Historial de auditoría */}
              <section className="border-t border-slate-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowAuditoria(v => !v)}
                  className="w-full flex items-center justify-between px-5 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-gray-700/40 transition-colors"
                >
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                    {t('pages.solicitudes.sections.auditoria')}
                    {auditoria.length > 0 && (
                      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-slate-200 dark:bg-gray-600 text-slate-600 dark:text-slate-300 text-[10px] font-bold">
                        {auditoria.length}
                      </span>
                    )}
                  </span>
                  <svg
                    className={`w-4 h-4 text-slate-400 transition-transform duration-150 ${showAuditoria ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showAuditoria && (
                  <div className="px-5 pb-3">
                    {auditoriaLoading ? (
                      <p className="text-xs text-slate-400">{t('common.loading')}</p>
                    ) : auditoria.length === 0 ? (
                      <p className="text-xs text-slate-400 italic">{t('pages.solicitudes.auditoria.empty')}</p>
                    ) : (
                      <ol className="relative border-l border-slate-200 dark:border-gray-700 ml-2 space-y-3">
                        {auditoria.map(entry => (
                          <li key={entry.id} className="ml-4">
                            <span className="absolute -left-1.5 mt-1 w-3 h-3 rounded-full border-2 border-white dark:border-gray-800 bg-slate-400 dark:bg-gray-500" />
                            <div className="flex items-baseline gap-2 flex-wrap">
                              <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${accionBadge(entry.accion)}`}>
                                {t(`pages.solicitudes.auditoria.acciones.${entry.accion}`, { defaultValue: entry.accion })}
                              </span>
                              <span className="text-xs font-medium text-slate-700 dark:text-slate-300">{entry.usuario}</span>
                              <span className="text-xs text-slate-400 dark:text-slate-500">{fmtDate(entry.fecha)}</span>
                            </div>
                            {entry.detalle && (
                              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 ml-0.5 italic">{entry.detalle}</p>
                            )}
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                )}
              </section>
            </div>

            {/* Acciones — fijas al pie del panel */}
          </div>
        )}
      </div>

      {/* Modal de notas internas */}
      {showNotasModal && notasTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-1">
              {t('pages.solicitudes.notasModal.title')}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 truncate">
              {notasTarget.nombre_clinica}
            </p>

            {/* Toggle flag */}
            <button
              onClick={() => setNotasFlagged(f => !f)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium mb-4 transition-colors w-full ${
                notasFlagged
                  ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
                  : 'border-slate-300 dark:border-gray-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700'
              }`}
            >
              <span className="text-base">{notasFlagged ? '⚑' : '⚐'}</span>
              {notasFlagged ? t('pages.solicitudes.notasModal.flagged') : t('pages.solicitudes.notasModal.notFlagged')}
            </button>

            <textarea
              value={notasText}
              onChange={e => setNotasText(e.target.value)}
              rows={5}
              placeholder={t('pages.solicitudes.notasModal.placeholder')}
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-slate-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-amber-400 focus:border-transparent resize-none"
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setShowNotasModal(false)}
                className="btn btn-secondary flex-1"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSaveNotas}
                disabled={savingNotas}
                className="btn flex-1 bg-amber-500 text-white hover:bg-amber-600 disabled:bg-amber-300 focus:ring-amber-400"
              >
                {savingNotas ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de aprobación */}
      {showApproveModal && approveTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
              {t('pages.solicitudes.approveModal.title')}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">
              {t('pages.solicitudes.approveModal.subtitle', { nombre: approveTarget.nombre_clinica })}
            </p>
            <p className="text-xs text-slate-400 dark:text-slate-500 mb-6">
              {processingId === approveTarget.id
                ? t('pages.solicitudes.approveModal.processingHint')
                : t('pages.solicitudes.approveModal.waitHint')}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowApproveModal(false)}
                disabled={processingId === approveTarget.id}
                className="btn btn-secondary flex-1"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleAprobar}
                disabled={processingId === approveTarget.id}
                className="btn flex-1 bg-green-600 text-white hover:bg-green-700 disabled:bg-green-400 focus:ring-green-500"
              >
                {processingId === approveTarget.id
                  ? t('pages.solicitudes.processing')
                  : t('pages.solicitudes.actions.approve')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de rechazo */}
      {showRejectModal && rejectTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
              {t('pages.solicitudes.rejectModal.title')}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              {t('pages.solicitudes.rejectModal.subtitle', { nombre: rejectTarget.nombre_clinica })}
            </p>
            <textarea
              value={rejectNotas}
              onChange={e => setRejectNotas(e.target.value)}
              rows={4}
              placeholder={t('pages.solicitudes.rejectModal.notesPlaceholder')}
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-slate-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-red-400 focus:border-transparent resize-none"
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setShowRejectModal(false)}
                className="btn btn-secondary flex-1"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleRechazar}
                className="btn btn-danger flex-1"
              >
                {t('pages.solicitudes.actions.reject')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Componente auxiliar ──────────────────────────────────────────────────────
const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-0.5">{label}</p>
    <p className="text-sm text-slate-900 dark:text-white break-words">{value}</p>
  </div>
);
